/**
 * Document extracts API — wraps the extracts surface of the `/files` router
 * (frontend_documentation/document-extracts-viewer.md).
 *
 * Powers the 3-pane extracted-document viewer: the source file, its OCR markdown,
 * and the structured JSON + extracted images. Extracted artifacts live ONLY as
 * blobs in the `{dealId}-extracted` container (no loan_documents row), addressed
 * by `blob_name` — never a Mongo id.
 *
 * Uses the shared `losHttp` client (same backend as the rest of /files). Byte
 * endpoints return Blobs; callers create/revoke object URLs.
 */
import { losHttp } from "../lib/losHttp";

const enc = (blobName) => encodeURIComponent(blobName);

export const losExtractsApi = {
  // GET /files/extracts/{dealId} → viewer index (parents + their extracts + edit state).
  getIndex: (dealId) =>
    losHttp.get(`/files/extracts/${dealId}`).then((r) => r.data),

  // GET /files/extract_bytes/{dealId}/{blob_name}[?original=true] → text (md/json).
  // Serves the EDITED copy unless `original` is set.
  getText: (dealId, blobName, { original = false } = {}) =>
    losHttp
      .get(`/files/extract_bytes/${dealId}/${enc(blobName)}`, {
        params: original ? { original: "true" } : {},
        responseType: "text",
        transformResponse: [(d) => d], // keep raw string (don't JSON.parse)
      })
      .then((r) => r.data),

  // GET /files/extract_bytes/... as a Blob → object URL (images).
  getBlobUrl: (dealId, blobName, { original = false } = {}) =>
    losHttp
      .get(`/files/extract_bytes/${dealId}/${enc(blobName)}`, {
        params: original ? { original: "true" } : {},
        responseType: "blob",
      })
      .then((r) => URL.createObjectURL(r.data)),

  // PUT /files/extract_edit/{dealId}/{blob_name} { content?, notes? }
  // content = string (markdown) | object (structured_json). Returns merged edit state.
  saveEdit: (dealId, blobName, { content, notes } = {}) =>
    losHttp
      .put(`/files/extract_edit/${dealId}/${enc(blobName)}`, { content, notes })
      .then((r) => r.data),

  // GET /files/file_by_id/{dealId}/{file_storage_name}?inline=true → source bytes
  // as a Blob → object URL (left pane: the original PDF/doc).
  getSourceBlobUrl: (dealId, fileStorageName) =>
    losHttp
      .get(`/files/file_by_id/${dealId}/${fileStorageName}`, {
        params: { inline: "true" },
        responseType: "blob",
      })
      .then((r) => URL.createObjectURL(r.data)),
};

export default losExtractsApi;
